import { Injectable, Injector, inject } from '@angular/core';
import { ActivatedRoute, Navigation, NavigationExtras, Router, RoutesRecognized } from '@angular/router';
import { HisSessionStorageKeys } from '../../model/his-session-storage-keys';
import { HisSessionStorageService } from './his-session-storage.service';
import { filter } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { Location } from '@angular/common';
import { HisBaseGlobalsService } from './his-base-globals.service';
import { HisRouteService } from '../routing/his-route.service';
import { ensurePrefix } from '../../utils';
import { HisSearchNavigationService } from '../../../genericsearch/service/his-search-navigation.service';

/**
 * Service that allows to navigate to previous pages further back in history.
 *
 * All keys are stored in the browser sessionStorage to avoid that they get lost on redirects (refresh, language switch etc.).
 */
@Injectable({providedIn: 'root'})
export class HisNavigationService {
    private readonly router = inject( Router );
    private readonly sessionStorageService = inject( HisSessionStorageService );
    private readonly location = inject( Location );
    private readonly baseGlobalsService = inject( HisBaseGlobalsService );
    private readonly hisRouteService = inject( HisRouteService );
    private readonly injector = inject( Injector );
    private get searchNavigationService() {
        return this.injector.get( HisSearchNavigationService );
    } // ensures proper injection order for circular dependency

    private static readonly ANGULAR_CONTEXT = '/a/';
    private static readonly MAX_BACK_URLS = 20;

    // save the previous route
    public previousRoutePath = new BehaviorSubject<string>( '' );
    private currentRoutePath?: string;

    // Workaround: define the dirty state as static because HisNavigationService is provided multiple times
    private static isDirty = false;
    private static isDirtyTrackingActive = false;

    set dirty(d: boolean) {
        HisNavigationService.isDirty = d;
    }

    get dirty() {
        return HisNavigationService.isDirty;
    }

    set dirtyTracking( active: boolean ) {
        HisNavigationService.isDirtyTrackingActive = active;
    }

    get dirtyTracking() {
        return HisNavigationService.isDirtyTrackingActive;
    }

    /** Whether the user has modified data which would be lost on leaving the page. Always `false` while dirty tracking is not active. */
    get hasUnsavedChanges(): boolean {
        return this.dirtyTracking && this.dirty;
    }

    constructor() {
        // ..initial previous route will be the current path for now
        this.previousRoutePath.next( this.location.path() );

        // on every route change save the route we came from in a behaviour subject to access it in another component
        // we can use if another component like intro-advertise need the previous route
        // because he need to redirect the user to where he did came from.
        // Replacing / internal navigations do not leave the current page, so they only update the current route.
        this.router.events.pipe(
            filter( ( e ): e is RoutesRecognized => e instanceof RoutesRecognized )
        ).subscribe( e => {
            const info = this.router.currentNavigation()?.extras.info as NavigationInfo | undefined;
            if ( this.currentRoutePath !== undefined && !info?.skipPreviousRoutePathUpdate ) {
                this.previousRoutePath.next( this.currentRoutePath );
            }
            this.currentRoutePath = e.urlAfterRedirects;
        } );
    }

    /** redirect to JSF access-denied page, but forward current url via "re" parameter */
    public navigateToAccessDenied() {
        const url = this.getCurrentHref();
        const prefix = this.getQisserverUrl();
        const suffix = url.substring( prefix.length );
        this.redirectTo( prefix + '/pages/access-denied.faces?re=' + encodeURIComponent( suffix ) );
    }

    /** gets URl with qisserver path,
     * is used to calculate path to ckeditor scripts,
     * which are passed via argument in ckeditor-angular integration.
     * Returns an empty string if the current URL is not inside the angular context. */
    public getQisserverUrl(): string {
        return this.parseAngularUrl( this.getCurrentHref() )?.qisserverUrl ?? '';
    }

    /** @deprecated Use `HisSearchNavigationService` directly. */
    public setSearchUrl( searchUrl: string ) {
        this.searchNavigationService.setSearchUrl( searchUrl );
    }

    /** @deprecated Use `HisSearchNavigationService` directly. */
    public hasPreviousSearchUrl(): boolean {
        return this.searchNavigationService.hasPreviousSearchUrl();
    }

    /** @deprecated Use `HisSearchNavigationService` directly. */
    public navigateToLastSearchUrl(): Promise<void> {
        return this.searchNavigationService.navigateToLastSearchUrl();
    }

    /** @deprecated Use `HisSearchNavigationService` directly. */
    public setSearchResultUrl( searchResultUrl: string ) {
        this.searchNavigationService.setSearchResultUrl( searchResultUrl );
    }

    /** @deprecated Use `HisSearchNavigationService` directly. */
    public getPreviousSearchResultUrl(): string {
        return this.searchNavigationService.getPreviousSearchResultUrl();
    }

    /** @deprecated Use `HisSearchNavigationService` directly. */
    public hasPreviousSearchResultUrl(): boolean {
        return this.searchNavigationService.hasPreviousSearchResultUrl();
    }

    /** @deprecated Use `HisSearchNavigationService` directly. */
    public navigateToLastSearchResultUrl(): Promise<void> {
        return this.searchNavigationService.navigateToLastSearchResultUrl();
    }

    /** Navigates to a full browser URL via the router, redirecting the window if the URL is outside the angular module or the router fails. */
    public navigateToUrl( url: string ): Promise<void> {
        return this.navigateToStoredUrl( url );
    }

    /** Navigates via the router and falls back to a full page redirect if the router navigation fails. */
    private navigateToStoredUrl( url: string ): Promise<void> {
        const shortUrl = this.extractShortUrl( url );
        if ( shortUrl === undefined ) {
            this.redirectTo( url );
            return Promise.resolve();
        }
        return this.router.navigateByUrl( shortUrl ).then(
            (): void => undefined,
            ( error: unknown ): void => {
                console.warn( 'HisNavigationService: router navigation to ' + shortUrl + ' failed, using redirect', error );
                this.redirectTo( url );
            }
        );
    }

    private getCurrentHref(): string {
        return window.location.href;
    }

    private redirectTo( url: string ) {
        window.location.href = url;
    }

    /**
     * Splits a URL like `http://muster-head.de/qisserver/a/cs.sys.hisng/route?x=1#top` at the angular context.
     *
     * @return the parts of the URL, or `undefined` if it is not inside the angular context
     */
    private parseAngularUrl( url: string ): ParsedAngularUrl | undefined {
        const match = ANGULAR_URL_PATTERN.exec( url );
        if ( !match ) {
            return undefined;
        }
        const [, qisserverUrl, moduleName, rest] = match;
        return {
            qisserverUrl,
            moduleName,
            basePath: `${qisserverUrl}${HisNavigationService.ANGULAR_CONTEXT}${moduleName}`,
            route: ensurePrefix( rest, '/' )
        };
    }

    /**
     * Extract the part required by the router from a complete URL
     *
     * @param url full URL
     * @return the route part of the URL, or `undefined` if the URL is not inside the angular context
     */
    private extractShortUrl( url: string ): string | undefined {
        return this.parseAngularUrl( url )?.route;
    }

    public hasLastBackUrl(): boolean {
        return this.getLastBackUrl().length > 0 || window.history.length > 1;
    }

    /**
     * Navigates to the last back URL saved in the session storage: via router if it belongs to the same angular
     * module, otherwise by redirecting the window. The entry is only removed if the navigation was not cancelled,
     * e.g. by a guard asking for unsaved changes.
     * Falls back to `window.history.back()` if there is no saved back URL, which is not working reliable.
     */
    public async navigateToLastBackUrl(): Promise<void> {
        const backUrls = this.getLastBackUrl();
        if ( backUrls.length === 0 ) {
            window.history.back();
            return;
        }

        const lastBackUrl = backUrls[backUrls.length - 1];
        const remainingBackUrls = backUrls.slice( 0, -1 );
        const shortUrl = this.location.path() !== this.previousRoutePath.value && this.isSameContext( this.getCurrentHref(), lastBackUrl )
            ? this.extractShortUrl( lastBackUrl )
            : undefined;

        if ( shortUrl === undefined ) {
            // the redirect unloads the page, so the storage has to be updated first
            this.sessionStorageService.put( HisSessionStorageKeys.CS_SYS_LAST_BACK_URL, remainingBackUrls );
            this.redirectTo( lastBackUrl );
            return;
        }

        try {
            const navigated = await this.router.navigateByUrl( shortUrl );
            if ( navigated || shortUrl === this.router.url ) {
                this.sessionStorageService.put( HisSessionStorageKeys.CS_SYS_LAST_BACK_URL, remainingBackUrls );
            }
        } catch ( error ) {
            console.warn( 'HisNavigationService: router navigation to ' + shortUrl + ' failed, using redirect', error );
            this.sessionStorageService.put( HisSessionStorageKeys.CS_SYS_LAST_BACK_URL, remainingBackUrls );
            this.redirectTo( lastBackUrl );
        }
    }

    /** Whether both URLs belong to the same angular module, meaning the router can navigate between them. */
    private isSameContext( currentUrl: string, navigateToUrl: string ): boolean {
        const current = this.parseAngularUrl( currentUrl );
        const target = this.parseAngularUrl( navigateToUrl );
        return !!current && !!target && current.moduleName === target.moduleName;
    }

    /**
     * Pushes the URL onto the back stack, ignoring consecutive duplicates and dropping the oldest entries above the limit.
     * @param backUrl the page to return to later, defaults to the current page
     */
    public setLastBackUrl( backUrl: string = this.getCurrentHref() ) {
        const backUrls = this.getLastBackUrl();
        if ( backUrls[backUrls.length - 1] === backUrl ) {
            return;
        }
        backUrls.push( backUrl );
        this.sessionStorageService.put( HisSessionStorageKeys.CS_SYS_LAST_BACK_URL, backUrls.slice( -HisNavigationService.MAX_BACK_URLS ) );
    }

    private getLastBackUrl(): string[] {
        return this.sessionStorageService.get( HisSessionStorageKeys.CS_SYS_LAST_BACK_URL ) ?? [];
    }

    /**
     * @returns the URL up to and including the angular module, e.g. `http://localhost:8080/qisserver/a/cs.sys.hisng`,
     * or an empty string if the current URL is not inside the angular context
     */
    public getUrlBasePath(): string {
        return this.parseAngularUrl( this.getCurrentHref() )?.basePath ?? '';
    }

    public startDirtyTracking() {
        this.dirty = false;
        if (this.baseGlobalsService.conf['core.sys.ui.confirm_unsaved']) {
            this.dirtyTracking = true;
        }
    }

    public stopDirtyTracking() {
        this.dirtyTracking = false;
    }

    /**
     *
     * @returns the system's context path, may be with leading '/'. The context path is set by the server and given by PortalUtil.getContextPath. Example: '/qisserver'
     */
    public getContextPath(): string {
        return this.baseGlobalsService.system.contextPath;
    }

    /**
     * Updates the URL params of the current URL without navigating, meaning no new entry is made in the browser history.
     * Instead the URL in the existing history entry is replaced, preserving the previous page.
     * 
     * This can be used to add, change or remove query parameters in the URL for a semantically identical page and
     * preserve them for later navigation, essentially modifying the browsers memory of the current page.
     * @example
     * ```ts
     * // Base-URL: /path?a=1&b=2&c=3
     * 
     * updateUrlParamsWithoutBrowserNavigation( {
     *   queryParams: { b: '3', d: '4', e: '5' }
     * } );
     * // Resulting URL: /path?b=3&d=4&e=5 | default behaviour completely replaces existing params 
     * 
     * updateUrlParamsWithoutBrowserNavigation( {
     *   queryParams: { a: null, b: '5', d: '5' },
     *   queryParamsHandling: 'merge'
     * } );
     * // Resulting URL: /path?b=5&c=3&d=5 | a is removed; b is updated to '5'; c is kept; d is added
     * // Note: 'merge' is required to keep existing params not specified in 'queryParams'
     * 
     * updateUrlParamsWithoutBrowserNavigation( {
     *   queryParams: { b: '5', d: '5' },
     *   queryParamsHandling: 'preserve'
     * } );
     * // Resulting URL: /path?a=1&b=2&c=3 | params are preserved as is, queryParams are ignored
     * // Note: 'preserve' is meaningless in this method, as it would result in 0 changes to the URL;
     * // the router skips such a navigation and `success` is `false`
     * ```
     * The URL fragment is preserved by default. Pass `preserveFragment: false` to drop it or `fragment` to replace it.\
     * **Note:** When passing `preserveFragment: true` the `fragment` value is ignored and the current URL fragment is kept.
     * @param options params and their update handling
     * @returns Promise resolving to the result of the navigation attempt, if no errors occurred
     * @devNote While not adding to the browser history, this method does perform a navigation internally.
     * Reactions to `NavigationEvents` in the application are triggered as usual, except that
     * `this.previousRoutePath` is not updated.
     */
    public updateUrlParamsWithoutBrowserNavigation( options: UpdateUrlParamsOptions ) {
        const preserveFragment = options.preserveFragment ?? options.fragment === undefined;
        return this.execute( [], { ...options, preserveFragment }, { replaceUrl: true, skipLocationChange: false } );
    }

    /**
     * Updates the URL by navigating to the specified commands without adding a new entry in the browser history.
     * Instead the URL in the existing history entry is replaced, preserving the previous page.
     * 
     * This can be used to navigate to a different page or route stage, after the current state has changed and
     * became invalid. In such cases, we dont want to persist the _invalid_ state in the browser history, so that
     * the user cannot navigate back to it. Instead, the previous valid page is preserved in the history, together
     * with the new currently valid page.
     * 
     * A use case would be an edit mask, which after saving redirects to a view mode of the same page, where we want to
     * reflect this state change in the URL, but not preserve the previous `edit` state. In such a case, the user would
     * be directed back to the page before the edit mask when clicking the back button, for example a search result.\
     * So the URL would change from `item/123/edit` to `item/123/view` and a backwards navigation would skip the edit page.
     * 
     * @param commands the navigation commands as specified in the router documentation like `edit` or `123`
     * @param options modified options to apply to the navigation
     * @returns Promise resolving to the result of the navigation attempt, if no errors occurred
     */
    public updateRouteWithoutBrowserNavigation( commands: readonly any[], options: RouteNavigationOptions = {} ) {
        return this.execute( commands, options, { replaceUrl: true, skipLocationChange: false } );
    }

    /**
     * Navigates to the specified commands without affecting the browser state.
     * This is purely an angular internal navigation, where:
     * - no new history entry is created
     * - the URL in the browser is not updated / changed
     * 
     * The `routerState` still updates like normal, including `params` and `queryParams`, so as far as components are
     * concerned, a normal navigation took place.
     * 
     * This can be used to navigate to a transient state, which should not be reflected in the browser history or URL.
     * After the next normal navigation, the URL and history will not contain any trace of this internal navigation.
     * 
     * Could be used to switch between tabs defined in childoutlets, where the tab state should not be reflected or 
     * preserved in the URL or history.
     * @param commands the navigation commands as specified in the router documentation of {@linkcode Router.navigate}
     * @param options modified options to apply to the navigation
     * @returns Promise resolving to the result of the navigation attempt, if no errors occurred\
     * Note: `finalUrl` refers to actual URL of the internal router state, not the URL present in the browser, which is not updated here.
     * @devNote Use with care, as this can be confusing to users and developers if the URL does not reflect the current state.\
     * If a user copies the URL or bookmarks the page, they will be directed to the last page/state that was reflected by that URL.\
     * Code reading `window.location` or `Location.path()` still sees the browser URL, while `Router.url` reflects the internal state.
     */
    public navigateInternal( commands: readonly any[], options: RouteNavigationOptions = {} ) {
        return this.execute( commands, options, { replaceUrl: false, skipLocationChange: true } );
    }

    /**
     * A classic navigation that updates both the browser history and URL, just like entering a URL freshly in the browser.\
     * Considered the standard navigation method for programmatic page navigation.
     * @param commands the navigation commands as specified in the router documentation of {@linkcode Router.navigate}
     * @param options modified options to apply to the navigation
     * @returns Promise resolving to the result of the navigation attempt, if no errors occurred
     */
    public navigate( commands: readonly any[], options: RouteNavigationOptions = {} ) {
        return this.execute( commands, options, { replaceUrl: false, skipLocationChange: false } );
    }

    private async execute(
        commands: readonly any[],
        options: RouteNavigationOptions,
        flags: Pick<NavigationExtras, 'replaceUrl' | 'skipLocationChange'>
    ): Promise<NavigationResult> {
        const previousUrl = this.router.url;
        // `info` is not persisted in the history state, unlike `state`, so back/forward navigations are unaffected
        const info: NavigationInfo = { ...options.info, skipPreviousRoutePathUpdate: !!( flags.replaceUrl || flags.skipLocationChange ) };
        const success = await this.router.navigate( commands, {
            ...options,
            ...flags,
            relativeTo: this.parseRelativeTo( options.relativeTo ),
            info
        } );
        return {
            success,
            navigation: success ? this.router.lastSuccessfulNavigation() : undefined,
            previousUrl,
            finalUrl: this.router.url
        };
    }

    /**
     * Parses the `relativeTo` option into an actual `ActivatedRoute` instance.\
     * An `ActivatedRoute` is passed through as is, like in `Router.navigate`.\
     * Default is `'root'`, which is also used for `'parent'` if the current route is the root route itself.
     */
    private parseRelativeTo( relativeTo: RelativeToOption | undefined ): ActivatedRoute {
        if ( typeof relativeTo === 'object' && relativeTo !== null ) {
            // no `instanceof`, so that route stubs in tests are passed through as well
            return relativeTo;
        }
        switch ( relativeTo ) {
            case 'parent':
                return this.hisRouteService.getActivatedRoute().parent ?? this.router.routerState.root;
            case 'current':
                return this.hisRouteService.getActivatedRoute();
            case 'root':
            default:
                return this.router.routerState.root;
        }
    }
}

interface NavigationInfo {
    [key: string]: unknown;
    skipPreviousRoutePathUpdate: boolean
}

/** Matches the first `/a/<module>` segment; groups: qisserver URL, module name, remainder (route, query, fragment) */
const ANGULAR_URL_PATTERN = /^(.*?)\/a\/([^/?#]+)(.*)$/;

interface ParsedAngularUrl {
    /** e.g. `http://muster-head.de/qisserver` */
    qisserverUrl: string;
    /** e.g. `cs.sys.hisng` */
    moduleName: string;
    /** e.g. `http://muster-head.de/qisserver/a/cs.sys.hisng` */
    basePath: string;
    /** the part used by the router, always starting with `/` */
    route: string;
}
type UpdateUrlParamsOptions = Pick<NavigationExtras, 'queryParams' | 'queryParamsHandling' | 'fragment' | 'preserveFragment'>;
type RelativeToOption = 'root' | 'current' | 'parent' | ActivatedRoute;
type RouteNavigationOptions = Omit<NavigationExtras, 'relativeTo' | 'replaceUrl' | 'skipLocationChange' | 'info'> & {
    /** 
     * The base route to apply the navigation commands to.
     * - `'root'`: the root route
     * - `'current'`: the current activated route
     * - `'parent'`: the parent of the current activated route to easily switch between route siblings\
     * This refers to the actual parent object of the current activated route, not to be confused with the preceding URL path segment.
     * - an `ActivatedRoute` instance, used as is like in `Router.navigate`
     * 
     * Defaults to `'root'` in `parseRelativeTo`.
     */
    relativeTo?: RelativeToOption;
    /** Additional transient navigation info */
    info?: Partial<NavigationInfo>;
};

/**
 * Result of a navigation attempt without errors.
 * 
 * If the navigation failed without errors, `success` is `false` and only the `finalUrl` is available, pointing to the current (unchanged) URL.
 * 
 * If the navigation was successful, the {@linkcode Navigation} object is available, representing the navigation event.\
 * The `previousUrl` before the navigation started is provided in both cases.
 * 
 * @devNote Keep in mind that the `URL` in a navigation context only refers to the angular part of the URL, not the full browser `href`.\
 * Example: For `http://vmqa-head.his.de:8080/hisinone/a/cs.sys.hisng/genericSearchLiveDemo/search?navigationPosition=...`, the application is hosted under
 * `http://vmqa-head.his.de:8080/hisinone/a/cs.sys.hisng`, leaving the angular navigation context (and URL) to start at `/genericSearchLiveDemo/...`.
 */
interface NavigationResult {
    /** Whether the navigation was successful */
    success: boolean;
    /** The navigation object representing the navigation event, if successful */
    navigation?: Navigation;
    /** The URL before the navigation started */
    previousUrl: string;
    /** The URL after the navigation completed */
    finalUrl: string;
}
